import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MetricCardProps {
  title: string;
  value: number | string;
  previousValue?: number;
  icon: LucideIcon;
  href?: string;
  loading?: boolean;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const variantStyles = {
  default: 'bg-blue-50 text-blue-600',
  success: 'bg-emerald-50 text-emerald-600',
  warning: 'bg-amber-50 text-amber-600',
  danger: 'bg-red-50 text-red-600',
};

const trendStyles = {
  up: { icon: TrendingUp, color: 'text-emerald-600' },
  down: { icon: TrendingDown, color: 'text-red-600' },
  neutral: { icon: Minus, color: 'text-gray-400' },
};

function MetricCardContent({
  title,
  value,
  icon: Icon,
  loading,
  trend,
  trendLabel,
  variant = 'default',
}: MetricCardProps) {
  const TrendIcon = trend ? trendStyles[trend].icon : null;
  const trendColor = trend ? trendStyles[trend].color : '';

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          {loading ? (
            <div className="h-7 w-14 bg-gray-200 rounded animate-pulse" />
          ) : (
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
          )}
          {(trend || trendLabel) && !loading && (
            <div className={cn('flex items-center gap-1 text-xs', trendColor)}>
              {TrendIcon && <TrendIcon className="h-3 w-3" />}
              <span>{trendLabel}</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg',
            variantStyles[variant]
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export function MetricCard(props: MetricCardProps) {
  const { href, loading } = props;

  const card = (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 transition-all duration-150',
        href && !loading && 'cursor-pointer hover:border-gray-300 hover:shadow-sm'
      )}
    >
      <MetricCardContent {...props} />
    </div>
  );

  if (href && !loading) {
    return (
      <Link to={href} className="block">
        {card}
      </Link>
    );
  }

  return card;
}

export function MetricCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
          <div className="h-7 w-12 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-9 w-9 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    </div>
  );
}
